import type { LatLngBounds, Map } from "leaflet";

// 300dpiを等倍（printScale = 1）の基準とする。
export const PRINT_STYLE_BASE_DPI = 300;

export interface PrintRenderContext {
  printScale: number;
  zoomOffset: number;
  referenceZoom: number;
  captureZoom: number;
}

export const getPrintRenderScale = (dpi: number): number => {
  if (!Number.isFinite(dpi) || dpi <= 0) {
    throw new RangeError("dpi must be a positive finite number");
  }
  return dpi / PRINT_STYLE_BASE_DPI;
};

export const createPrintRenderContext = ({
  dpi,
  referenceZoom,
  captureZoom,
}: {
  dpi: number;
  referenceZoom: number;
  captureZoom: number;
}): PrintRenderContext => ({
  printScale: getPrintRenderScale(dpi),
  zoomOffset: captureZoom - referenceZoom,
  referenceZoom,
  captureZoom,
});

/**
 * 指定した pixel size に bounds 全体を収める Leaflet ズームを求める。
 *
 * zoom 0 の投影座標を基準にすることで、Leaflet のズームごとの 2 倍スケールを
 * 明示的に計算し、現在の地図コンテナの大きさへ依存しない。
 */
export const getBoundsZoomForSize = (
  map: Pick<Map, "project" | "getMinZoom" | "getMaxZoom" | "options">,
  bounds: LatLngBounds,
  size: { width: number; height: number }
): number => {
  const northWest = map.project(bounds.getNorthWest(), 0);
  const southEast = map.project(bounds.getSouthEast(), 0);
  const boundsWidth = Math.abs(southEast.x - northWest.x);
  const boundsHeight = Math.abs(southEast.y - northWest.y);
  const scale = Math.min(size.width / boundsWidth, size.height / boundsHeight);
  const zoom = Math.log2(scale);
  // ヘッドレス環境でもブラウザと同じ基準zoomになるよう、設定値を直接使う。
  const zoomSnap = map.options.zoomSnap ?? 0;
  const roundedZoom =
    zoomSnap > 0
      ? Math.round(zoom / (zoomSnap / 100)) * (zoomSnap / 100)
      : zoom;
  // LeafletのgetBoundsZoomと同じ切り下げにし、zoomOffsetのずれを防ぐ。
  const snappedZoom =
    zoomSnap > 0 ? Math.floor(roundedZoom / zoomSnap) * zoomSnap : roundedZoom;

  return Math.min(map.getMaxZoom(), Math.max(map.getMinZoom(), snappedZoom));
};
